/**
 * Phase 9D: Operator View Transformer
 * 
 * Transforms raw supervised workflow response into clean, executive-ready presentation.
 * No new intelligence — only presentation changes.
 */

// =============================================================================
// OPERATOR OUTPUT CONTRACT
// =============================================================================

export interface OperatorWorkflowView {
  title: string;
  subtitle?: string;

  // Top banner
  quality: {
    status: 'PASS' | 'DEGRADED' | 'FAIL';
    score: number;
    strictness: string;
    coveragePctKnown: number;
    known: number;
    unavailable: number;
    missing: number;
    conflicts: number;
    knownWithoutLineage: number;
    why?: string;
  };

  // Primary sections
  whatHappened: Array<{ label: string; value?: string; linkedAssertionIds?: string[] }>;
  keyRisks: Array<{ title: string; detail?: string; linkedAssertionIds?: string[] }>;
  whatToDo: Array<{
    title: string;
    owner?: string;
    timeHorizon?: string;
    rationale?: string;
    linkedAssertionIds: string[];
  }>;
  whatsMissing: Array<{ metricKey: string; label: string; reason?: string }>;

  // Execution info (for debug toggle)
  executiveSummary: string;

  // Evidence + internals
  runId: string;
  workflowId: string;
  evidenceBundleId?: string | null;
}

// =============================================================================
// INPUT TYPES (from supervised response)
// =============================================================================

interface SupervisedRunResponse {
  success: boolean;
  workflow: {
    id: string;
    name: string;
    description?: string;
  };
  runId?: string;
  final?: SupervisedFinal;
}

// Phase 10B: DQE output types (from backend)
interface DQENormalizedMetric {
  metricKey: string;
  label: string;
  value: number | null;
  unit: string;
  dataStatus: 'known' | 'unavailable';
  flags?: string[];
  displayValue: string;
  linkedAssertionIds: string[];
}

interface DQERecommendation {
  type: string;
  title: string;
  ownerRole: string;
  timeHorizon: string;
  rationale: string;
  linkedAssertionIds: string[];
  confidence: 'high' | 'medium' | 'low';
}

interface DQEOutput {
  whatHappened: DQENormalizedMetric[];
  conclusions: string[];
  recommendations: DQERecommendation[];
  missingButNonBlocking: Array<{ metricKey: string; reason: string }>;
  autoRepairActions: Array<{ type: string; title: string; details: unknown }>;
}

interface SupervisedFinal {
  executiveSummary: string;
  decisionPlan: {
    topActions: Array<{
      title: string;
      rationale: string;
      linkedAssertions: string[];
      recommendedOwnerRole?: 'Founder' | 'Retail' | 'Marketing' | 'Ops';
      timeHorizon: 'today' | 'this_week' | 'this_month';
    }>;
  };
  evidence: {
    assertions: EvidenceAssertion[];
    lineage?: unknown[];
    warnings?: string[];
  };
  conflicts?: Array<{
    metricKey: string;
    unit?: string;
    values: Array<number | string | null>;
    assertionIds: string[];
  }>;
  needsReview?: boolean;
  quality?: QualityPayload;
  // Phase 10B: DQE output for weekly_retail_operating_review
  operatorView?: DQEOutput;
}

interface EvidenceAssertion {
  id: string;
  label: string;
  metricKey: string;
  value: number | string | null;
  unit?: string;
  dataStatus: 'known' | 'unavailable';
  lineageRefIds: string[];
  notes?: string[];
}

interface QualityPayload {
  strictness: 'strict' | 'soft';
  status: 'PASS' | 'DEGRADED' | 'FAIL';
  score: number;
  requiredTotal?: number;
  presentKnown: number;
  presentUnavailable: number;
  knownWithoutLineageCount: number;
  coveragePctKnown: number;
  missing: string[];
  conflicts?: Array<{ metricKey: string; values: Array<number | string | null> }>;
  tenantScopeViolations?: string[];
  warnings?: string[];
}

// =============================================================================
// FORMAT HELPERS
// =============================================================================

function formatValue(value: number | string | null, unit?: string): string {
  if (value === null || value === undefined) return 'N/A';
  
  if (typeof value === 'number') {
    // Currency formatting
    if (unit === 'usd' || unit === 'dollars' || unit?.toLowerCase().includes('dollar')) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
    }
    // Percentage formatting
    if (unit === 'percent' || unit === 'pct' || unit === '%') {
      return `${(value * 100).toFixed(1)}%`;
    }
    // Units formatting
    if (unit === 'units') {
      return new Intl.NumberFormat('en-US').format(value) + ' units';
    }
    // Default number formatting
    if (value >= 1000) {
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
    }
    return value.toFixed(2);
  }
  
  return String(value);
}

/**
 * Priority metric prefixes for "What Happened" section.
 * Order matters — first matches are shown first.
 */
const WHAT_HAPPENED_PRIORITY_PREFIXES = [
  'retail.revenue',
  'retail.sales',
  'retail.wow',
  'retail.yoy',
  'retail.comp',
  'inventory.risk',
  'inventory.oh',
  'inventory.stockout',
  'forecast.accuracy',
  'campaign.impact',
  'buyer.score',
];

/**
 * Keywords that indicate a risk-related assertion
 */
const RISK_KEYWORDS = ['risk', 'stockout', 'decline', 'oos', 'low', 'negative', 'alert', 'warning'];

// =============================================================================
// MAIN TRANSFORMER
// =============================================================================

export function buildOperatorWorkflowView(response: SupervisedRunResponse): OperatorWorkflowView | null {
  if (!response.final) {
    return null;
  }

  const { final, workflow, runId } = response;
  const { decisionPlan, evidence, quality, conflicts, operatorView: dqeOutput } = final;

  // -------------------------------------------------------------------------
  // Quality Banner
  // -------------------------------------------------------------------------
  const qualityView = {
    status: quality?.status ?? 'PASS',
    score: quality?.score ?? 100,
    strictness: quality?.strictness ?? 'soft',
    coveragePctKnown: quality?.coveragePctKnown ?? 1,
    known: quality?.presentKnown ?? 0,
    unavailable: quality?.presentUnavailable ?? 0,
    missing: quality?.missing?.length ?? 0,
    conflicts: (quality?.conflicts?.length ?? 0) + (conflicts?.length ?? 0),
    knownWithoutLineage: quality?.knownWithoutLineageCount ?? 0,
    why: buildQualityWhyText(quality),
  };

  // -------------------------------------------------------------------------
  // Phase 10B: If DQE output available, use it for formatted sections
  // -------------------------------------------------------------------------
  if (dqeOutput) {
    // DQE provides formatted What Happened with proper currency/percent display
    const whatHappenedDQE = dqeOutput.whatHappened.map(m => ({
      label: m.label,
      value: m.displayValue, // Already formatted by DQE (no [object Object], proper $, %)
      linkedAssertionIds: m.linkedAssertionIds,
    }));

    // DQE conclusions become summary items in keyRisks (if any risks)
    const keyRisksDQE = dqeOutput.recommendations
      .filter(r => r.type === 'inventory' || r.type === 'data_quality' || r.confidence === 'low')
      .slice(0, 4)
      .map(r => ({
        title: r.title,
        detail: r.rationale,
        linkedAssertionIds: r.linkedAssertionIds,
      }));

    // DQE recommendations become What To Do (typed, with owner/timeHorizon)
    const whatToDoDQE = dqeOutput.recommendations.map(r => ({
      title: r.title,
      owner: r.ownerRole,
      timeHorizon: r.timeHorizon?.replace('_', ' '),
      rationale: r.rationale + (r.confidence !== 'high' ? ` (${r.confidence} confidence)` : ''),
      linkedAssertionIds: r.linkedAssertionIds,
    }));

    // DQE missing-but-non-blocking + autoRepairActions
    const whatsMissingDQE = dqeOutput.missingButNonBlocking.slice(0, 5).map(m => ({
      metricKey: m.metricKey,
      label: formatMetricKeyAsLabel(m.metricKey),
      reason: m.reason,
    }));

    return {
      title: workflow.name,
      subtitle: workflow.description,
      quality: qualityView,
      whatHappened: whatHappenedDQE,
      keyRisks: keyRisksDQE,
      whatToDo: whatToDoDQE,
      whatsMissing: whatsMissingDQE,
      executiveSummary: dqeOutput.conclusions.join(' • ') || final.executiveSummary || '',
      runId: runId || 'unknown',
      workflowId: workflow.id,
      evidenceBundleId: null,
    };
  }

  // -------------------------------------------------------------------------
  // FALLBACK: No DQE output — use original logic
  // -------------------------------------------------------------------------

  // What Happened — Top business-meaningful known assertions
  const knownAssertions = (evidence?.assertions || []).filter(a => a.dataStatus === 'known');
  
  // Score and sort by priority
  const scoredAssertions = knownAssertions.map(a => ({
    assertion: a,
    priorityScore: getPriorityScore(a.metricKey),
  }));
  
  scoredAssertions.sort((a, b) => a.priorityScore - b.priorityScore);
  
  const whatHappened = scoredAssertions.slice(0, 6).map(({ assertion }) => ({
    label: assertion.label,
    value: formatValue(assertion.value, assertion.unit),
    linkedAssertionIds: [assertion.id],
  }));

  // -------------------------------------------------------------------------
  // Key Risks — From decision plan + risk-tagged assertions
  // -------------------------------------------------------------------------
  const keyRisks: OperatorWorkflowView['keyRisks'] = [];

  // From decision plan items (if any tagged as risk, or contain risk keywords)
  for (const action of decisionPlan?.topActions || []) {
    const titleLower = action.title.toLowerCase();
    const rationaleLower = (action.rationale || '').toLowerCase();
    const isRiskRelated = RISK_KEYWORDS.some(kw => titleLower.includes(kw) || rationaleLower.includes(kw));
    
    if (isRiskRelated && keyRisks.length < 5) {
      keyRisks.push({
        title: action.title,
        detail: action.rationale,
        linkedAssertionIds: action.linkedAssertions,
      });
    }
  }

  // From evidence assertions with risk keywords
  for (const assertion of knownAssertions) {
    const keyLower = assertion.metricKey.toLowerCase();
    const labelLower = assertion.label.toLowerCase();
    const isRiskRelated = RISK_KEYWORDS.some(kw => keyLower.includes(kw) || labelLower.includes(kw));
    
    if (isRiskRelated && keyRisks.length < 5) {
      // Avoid duplicates
      const alreadyIncluded = keyRisks.some(r => r.linkedAssertionIds?.includes(assertion.id));
      if (!alreadyIncluded) {
        keyRisks.push({
          title: assertion.label,
          detail: assertion.notes?.join('; '),
          linkedAssertionIds: [assertion.id],
        });
      }
    }
  }

  // -------------------------------------------------------------------------
  // What To Do — Directly from decision plan
  // -------------------------------------------------------------------------
  const whatToDo = (decisionPlan?.topActions || []).map(action => ({
    title: action.title,
    owner: action.recommendedOwnerRole,
    timeHorizon: action.timeHorizon?.replace('_', ' '),
    rationale: action.rationale,
    linkedAssertionIds: action.linkedAssertions || [],
  }));

  // -------------------------------------------------------------------------
  // What's Missing — Unavailable assertions + missing required metrics
  // -------------------------------------------------------------------------
  const whatsMissing: OperatorWorkflowView['whatsMissing'] = [];

  // From unavailable assertions
  const unavailableAssertions = (evidence?.assertions || []).filter(a => a.dataStatus === 'unavailable');
  for (const assertion of unavailableAssertions) {
    whatsMissing.push({
      metricKey: assertion.metricKey,
      label: assertion.label,
      reason: assertion.notes?.join('; ') || 'Data unavailable',
    });
  }

  // From quality.missing (required but not present at all)
  for (const missingKey of quality?.missing || []) {
    // Avoid duplicates
    if (!whatsMissing.some(m => m.metricKey === missingKey)) {
      whatsMissing.push({
        metricKey: missingKey,
        label: formatMetricKeyAsLabel(missingKey),
        reason: 'Required metric not extracted',
      });
    }
  }

  // -------------------------------------------------------------------------
  // Return normalized view
  // -------------------------------------------------------------------------
  return {
    title: workflow.name,
    subtitle: workflow.description,
    quality: qualityView,
    whatHappened,
    keyRisks,
    whatToDo,
    whatsMissing,
    executiveSummary: final.executiveSummary || '',
    runId: runId || 'unknown',
    workflowId: workflow.id,
    evidenceBundleId: null, // Can be extended if needed
  };
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function buildQualityWhyText(quality?: QualityPayload): string | undefined {
  if (!quality) return undefined;
  if (quality.status === 'PASS') return undefined;

  const parts: string[] = [];
  
  if (quality.presentUnavailable > 0) {
    parts.push(`${quality.presentUnavailable} metric(s) unavailable`);
  }
  if (quality.missing?.length > 0) {
    parts.push(`${quality.missing.length} required metric(s) missing`);
  }
  if ((quality.conflicts?.length || 0) > 0) {
    parts.push(`${quality.conflicts!.length} conflict(s) detected`);
  }
  if (quality.knownWithoutLineageCount > 0) {
    parts.push(`${quality.knownWithoutLineageCount} unlinked assertion(s)`);
  }
  if ((quality.tenantScopeViolations?.length || 0) > 0) {
    parts.push(`${quality.tenantScopeViolations!.length} scope violation(s)`);
  }

  return parts.length > 0 ? parts.join(' • ') : undefined;
}

function getPriorityScore(metricKey: string): number {
  const keyLower = metricKey.toLowerCase();
  
  for (let i = 0; i < WHAT_HAPPENED_PRIORITY_PREFIXES.length; i++) {
    if (keyLower.startsWith(WHAT_HAPPENED_PRIORITY_PREFIXES[i])) {
      return i;
    }
  }
  
  return WHAT_HAPPENED_PRIORITY_PREFIXES.length; // Low priority for unmatched
}

function formatMetricKeyAsLabel(metricKey: string): string {
  // Convert "retail.revenue_ty" to "Retail Revenue TY"
  return metricKey
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

